// drift-test:no-test-needed: visual-only ticker UI with no business logic; the only state is a shared module-level frame counter.
import { useEffect, useState } from "react";

// Terminal-style rotating spinner: -, \, |, / on a ~120ms cycle. Rendered
// in place of the static WAIT/OK badge on rows that the live reducer
// shows as currently running (sub-call has at least one attempt but no
// outcome yet; bridge step has no recorded duration_ms or error yet).
//
// One shared interval drives every mounted spinner so all in-flight rows
// rotate in lock-step (visually nicer than per-row drift) and we don't
// pay N interval callbacks per tick. The ticker only runs while at least
// one Spinner is mounted; it stops when the last consumer unmounts.

const FRAMES = ["-", "\\", "|", "/"] as const;
const FRAME_MS = 120;

let currentFrame = 0;
let timerId: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function ensureTicker(): void {
  if (timerId != null) return;
  timerId = setInterval(() => {
    currentFrame = (currentFrame + 1) % FRAMES.length;
    listeners.forEach((fn) => fn());
  }, FRAME_MS);
}

function maybeStopTicker(): void {
  if (listeners.size === 0 && timerId != null) {
    clearInterval(timerId);
    timerId = null;
  }
}

export function Spinner({ className = "" }: { className?: string }) {
  const [, force] = useState(0);
  useEffect(() => {
    const sub = () => force((n) => n + 1);
    listeners.add(sub);
    ensureTicker();
    return () => {
      listeners.delete(sub);
      maybeStopTicker();
    };
  }, []);
  return (
    <span aria-label="running" title="running" className={className}>
      {FRAMES[currentFrame]}
    </span>
  );
}
